import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    // Nuclear reset for stuck scroll issues (e.g. from Lenis or Modals)
    document.body.style.overflow = 'unset';
    document.documentElement.style.overflow = 'unset';
    document.body.classList.remove('lenis-stopped');
    document.documentElement.classList.remove('lenis-stopped');
  }, [pathname]);
  return null;
}
import { supabase } from './lib/supabase';
import { GlobalErrorBoundary } from './components/ErrorBoundary';
import { PromptMemory, ResponseCache } from './lib/ai.js';
import AnnouncementsBanner from './components/AnnouncementsBanner';

// Lazy load pages for better performance and to isolate chart-related errors
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Graveyard = lazy(() => import('./pages/Graveyard'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const CommunityFeed = lazy(() => import('./pages/CommunityFeed'));
const PublicProfile = lazy(() => import('./pages/PublicProfile'));
const WrappedPage = lazy(() => import('./pages/WrappedPage'));
const ReferralPage = lazy(() => import('./pages/ReferralPage'));
const PromptBattlePage = lazy(() => import('./pages/PromptBattlePage'));
const ScoreCardPage = lazy(() => import('./pages/ScoreCardPage'));
const IdeaRemixerPage = lazy(() => import('./pages/IdeaRemixerPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const CreativeGallery = lazy(() => import('./pages/CreativeGallery'));
const PromptSharePage = lazy(() => import('./pages/PromptSharePage'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const HowItWorks = lazy(() => import('./pages/HowItWorks'));
const Features = lazy(() => import('./pages/Features'));
const ForumPage = lazy(() => import('./pages/ForumPage'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const HistoryPage = lazy(() => import('./pages/HistoryPage'));

// SEO Pages
const PromptGenerator = lazy(() => import('./pages/seo/PromptGenerator'));
const AIPromptGenerator = lazy(() => import('./pages/seo/AIPromptGenerator'));
const ChatGPTPromptGenerator = lazy(() => import('./pages/seo/ChatGPTPromptGenerator'));
const MidjourneyPromptGenerator = lazy(() => import('./pages/seo/MidjourneyPromptGenerator'));
const ClaudePromptGenerator = lazy(() => import('./pages/seo/ClaudePromptGenerator'));
const AIImagePromptGenerator = lazy(() => import('./pages/seo/AIImagePromptGenerator'));
const PromptBuilder = lazy(() => import('./pages/seo/PromptBuilder'));
const PromptMaker = lazy(() => import('./pages/seo/PromptMaker'));
const PromptOptimizer = lazy(() => import('./pages/seo/PromptOptimizer'));
const CodingPromptGenerator = lazy(() => import('./pages/seo/CodingPromptGenerator'));
const MarketingPromptGenerator = lazy(() => import('./pages/seo/MarketingPromptGenerator'));
const WritingPromptGenerator = lazy(() => import('./pages/seo/WritingPromptGenerator'));

import MobileBottomNav from './components/MobileBottomNav';

function App() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    // Check for referral code in URL
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref');
    if (refCode) {
      localStorage.setItem('promptquill_referral_code', refCode);
    }

    const checkUser = async (currentSession) => {
      try {
        setSession(currentSession);
        if (currentSession?.user) {
          // Fetch profile
          const { data, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', currentSession.user.id)
            .maybeSingle();
          
          if (!error && data) {
            setProfile(data);
            setIsAdmin(data.role?.toUpperCase() === 'ADMIN');
          }
        } else {
          setProfile(null);
          setIsAdmin(false);
        }
      } catch (err) {
        console.error('Error checking user:', err);
      } finally {
        setLoading(false);
      }
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      checkUser(session);
    }).catch(() => setLoading(false));

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      checkUser(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  if (loading) return <div style={{ minHeight: '100vh', backgroundColor: '#080808' }} />;

  return (
    <GlobalErrorBoundary>
      <Router>
        <ScrollToTop />
        <AnnouncementsBanner />
        <Suspense fallback={<div style={{ minHeight: '100vh', backgroundColor: '#080808' }} />}>
          <Routes>
            <Route path="/" element={<LandingPage session={session} />} />
            <Route path="/prompt-generator" element={<PromptGenerator />} />
            <Route path="/ai-prompt-generator" element={<AIPromptGenerator />} />
            <Route path="/chatgpt-prompt-generator" element={<ChatGPTPromptGenerator />} />
            <Route path="/midjourney-prompt-generator" element={<MidjourneyPromptGenerator />} />
            <Route path="/claude-prompt-generator" element={<ClaudePromptGenerator />} />
            <Route path="/ai-image-prompt-generator" element={<AIImagePromptGenerator />} />
            <Route path="/prompt-builder" element={<PromptBuilder />} />
            <Route path="/prompt-maker" element={<PromptMaker />} />
            <Route path="/prompt-optimizer" element={<PromptOptimizer />} />
            <Route path="/coding-prompt-generator" element={<CodingPromptGenerator />} />
            <Route path="/marketing-prompt-generator" element={<MarketingPromptGenerator />} />
            <Route path="/writing-prompt-generator" element={<WritingPromptGenerator />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/ai" element={session ? <Dashboard session={session} /> : <Navigate to="/" />} />
            <Route path="/history" element={session ? <HistoryPage user={session?.user} /> : <Navigate to="/" />} />
            <Route path="/graveyard" element={session ? <Graveyard session={session} /> : <Navigate to="/" />} />
            <Route path="/admin" element={(session && isAdmin) ? <AdminPanel session={session} /> : <Navigate to="/ai" />} />
            <Route path="/settings" element={session ? <SettingsPage session={session} profile={profile} /> : <Navigate to="/" />} />
            <Route path="/support" element={session ? <SupportPage session={session} isAdmin={isAdmin} /> : <Navigate to="/" />} />
            <Route path="/pricing" element={<PricingPage session={session} />} />
            <Route path="/community" element={<CommunityFeed session={session} />} />
            <Route path="/gallery" element={<CreativeGallery session={session} />} />
            <Route path="/u/:username" element={<PublicProfile session={session} />} />
            <Route path="/shared/:id" element={<PromptSharePage session={session} />} />
            <Route path="/wrapped" element={session ? <WrappedPage session={session} /> : <Navigate to="/" />} />
            <Route path="/share/:promptId" element={<PromptSharePage session={session} />} />
            <Route path="/referral" element={session ? <ReferralPage session={session} /> : <Navigate to="/" />} />
            <Route path="/battles" element={session ? <PromptBattlePage session={session} /> : <Navigate to="/" />} />
            <Route path="/scorecard" element={session ? <ScoreCardPage session={session} /> : <Navigate to="/" />} />
            <Route path="/remixer" element={session ? <IdeaRemixerPage session={session} /> : <Navigate to="/" />} />
            <Route path="/blog" element={<BlogPage session={session} />} />
            <Route path="/blog/:slug" element={<BlogPage session={session} />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/cookies" element={<CookiePolicy />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/features" element={<Features />} />
            <Route path="/forums" element={<ForumPage session={session} />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Suspense>
        {session && <MobileBottomNav profile={profile} />}
      </Router>
    </GlobalErrorBoundary>
  );
}

export default App;
